/**
 * Brand attribution helpers for the admin dashboard.
 *
 * Two brands share one Firestore: Aero (the newer product: sites, user testing,
 * responsive) and Figmafolio (the original portfolio product). Url docs created
 * from Piece 1 onward carry an explicit `brand` field and are AUTHORITATIVE.
 * Older docs predate that field, so for them we INFER the brand from the signals
 * that already exist. Inference is best effort and APPROXIMATE: a historical doc
 * with no usable signal falls into the Figmafolio legacy bucket by design (Aero
 * is the newer brand, so anything unattributable is almost certainly Figmafolio).
 *
 * Do not treat an inferred brand as ground truth for historical records. The
 * forward `brand` field is the only authoritative source.
 */

/**
 * Infer the brand ('aero' | 'figmafolio') of a url doc's data.
 *
 * Priority order, first match wins:
 *   1. data.brand              -> authoritative (the Piece 1 forward field)
 *   2. data.aeroSite === true  -> 'aero' (AeroSites Figma-embed sites)
 *   3. data.userTesting object -> 'aero' (any UT config is an Aero test)
 *   3b. data.draftMeta present -> 'aero' (Aero createDraft always writes draftMeta;
 *       Figmafolio never does. Reliable on-doc signal for historical HTML sites.)
 *   4. isDraft 'fromPlugin' w/o userTesting -> 'figmafolio' (portfolio plugin)
 *   5. opts.signUpProduct (owner) if provided -> use it
 *   5b. type 'html' && isDraft === 'false' (string) -> 'figmafolio'
 *       (FolioForm handleChooseType always writes isDraft:"false"; Aero never does.)
 *   6. else -> 'figmafolio' (legacy bucket)
 *
 * @param {object} data - the url doc data
 * @param {object} [opts]
 * @param {string} [opts.signUpProduct] - owner's signUpProduct, if known
 * @returns {'aero' | 'figmafolio'}
 */
export function inferBrand(data, opts = {}) {
    const d = data || {};

    // 1. Forward field is authoritative.
    if (d.brand === 'aero' || d.brand === 'figmafolio') return d.brand;

    // 2. Aero HTML/Figma site marker.
    if (d.aeroSite === true) return 'aero';

    // 3. Any userTesting config (object present) is an Aero test, including a
    // claimed plugin test (which also carries isDraft 'fromPlugin').
    if (d.userTesting && typeof d.userTesting === 'object') return 'aero';

    // 3b. draftMeta is written by Aero's createDraft (createFlowActions.js) and
    // never by Figmafolio. Reliable Aero-positive signal for historical HTML sites
    // that predate the brand field.
    if (d.draftMeta && typeof d.draftMeta === 'object') return 'aero';

    // 4. Portfolio plugin doc: fromPlugin WITHOUT a userTesting object.
    if (d.isDraft === 'fromPlugin') return 'figmafolio';

    // 5. Fall back to the owner's signup product when the caller has it.
    if (opts.signUpProduct === 'aero' || opts.signUpProduct === 'figmafolio') {
        return opts.signUpProduct;
    }

    // 5b. Figmafolio HTML sites created via FolioForm handleChooseType always
    // carry isDraft:"false" (string). Aero's create path uses a separate draft
    // boolean and never writes isDraft. Apply only to type:'html' to avoid
    // misclassifying any legacy Figma folios that may also carry the field.
    if (d.type === 'html' && d.isDraft === 'false') return 'figmafolio';

    // 6. Legacy bucket: unattributable historical docs default to Figmafolio.
    return 'figmafolio';
}

/**
 * True when a url doc represents a published/live site: an HTML site has an
 * activeVersion, or a Figma site carries figma url(s).
 *
 * MUST mirror isPublishedUrlDoc() in functions/index.js (computeStatsRollup) so
 * the admin list, the detail panel, and the rollup BAN counts never disagree.
 *
 * @param {object} d - the url doc data
 * @returns {boolean}
 */
export function isPublishedUrlDoc(d) {
    if (!d) return false;
    if (d.activeVersion) return true;
    const urls = d.urls || {};
    if (urls.figmaDesktopUrl || urls.figmaMobileUrl || urls.figmaUrl) return true;
    if (d.figmaUrl || d.figmaDesktopUrl || d.figmaMobileUrl) return true;
    return false;
}

/**
 * Classify a url doc as 'test' | 'html' | 'figma' using the SAME rules as the
 * nightly rollup (computeStatsRollup in functions/index.js), so the admin's
 * per-user list summary and the user-detail panel always agree with the rollup
 * BAN counts (fixes the "list says 1 figma site, detail says 1 user test" bug).
 *
 * A doc is a TEST only when it carries a userTesting object AND either
 * userTesting.enabled === true OR it has no site signal: an unenabled
 * userTesting:{enabled:false} stub on a doc that has since become a real site
 * (Aero's draft creation writes the stub at birth) is a SITE, not a test.
 * Otherwise it is an HTML site (type 'html') or a FIGMA site (everything else).
 *
 * @param {object} data - the url doc data
 * @returns {'test' | 'html' | 'figma'}
 */
export function classifyUrlItem(data) {
    const d = data || {};
    const hasSiteSignal =
        d.type === 'html' || d.type === 'figma' || isPublishedUrlDoc(d);
    const isTest =
        d.userTesting != null &&
        typeof d.userTesting === 'object' &&
        (d.userTesting.enabled === true || !hasSiteSignal);
    if (isTest) return 'test';
    return d.type === 'html' ? 'html' : 'figma';
}

/**
 * Infer which plugin (if any) a record originated from.
 *
 * Collection rules (when the caller knows the source collection, pass it):
 *   - pluginUserTesting collection  -> 'aero-ut'   (entirely Aero user testing)
 *   - pluginFolio collection        -> 'figmafolio-plugin'
 *   - pluginResponsiveAuth /
 *     responsiveUsage collections   -> 'aero-responsive'
 *
 * Url-doc field rules (when classifying a url doc):
 *   - isDraft 'fromPlugin' WITH userTesting    -> 'aero-ut' (claimed UT test)
 *   - isDraft 'fromPlugin' WITHOUT userTesting -> 'figmafolio-plugin'
 *   - otherwise                                -> 'web'
 *
 * Like inferBrand, this is approximate for historical url docs.
 *
 * @param {object} data - the record data
 * @param {object} [opts]
 * @param {string} [opts.collection] - source collection name, if known
 * @returns {'figmafolio-plugin' | 'aero-ut' | 'aero-responsive' | 'web'}
 */
export function inferPluginOrigin(data, opts = {}) {
    const d = data || {};

    // Collection is the strongest signal when the caller supplies it.
    switch (opts.collection) {
        case 'pluginUserTesting':
            return 'aero-ut';
        case 'pluginFolio':
            return 'figmafolio-plugin';
        case 'pluginResponsiveAuth':
        case 'responsiveUsage':
            return 'aero-responsive';
        default:
            break;
    }

    // Url-doc classification by field shape.
    if (d.isDraft === 'fromPlugin') {
        const hasUserTesting = d.userTesting && typeof d.userTesting === 'object';
        return hasUserTesting ? 'aero-ut' : 'figmafolio-plugin';
    }

    return 'web';
}

// Human-readable labels for plugin origins, for table cells.
export const PLUGIN_ORIGIN_LABELS = {
    'figmafolio-plugin': 'Figmafolio plugin',
    'aero-ut': 'Aero UT plugin',
    'aero-responsive': 'Aero Responsive plugin',
    web: 'Web',
};

// Display name for a brand key.
const BRAND_DISPLAY = { aero: 'Aero', figmafolio: 'Figmafolio' };

/**
 * Human label combining the row's BRAND and its CHANNEL (web vs plugin), e.g.
 * "Aero web", "Aero plugin", "Figmafolio web", "Figmafolio plugin".
 *
 * Brand comes from inferBrand (authoritative `brand` field when present, else
 * inference). Channel keeps the existing web-vs-plugin distinction: any
 * plugin-origin doc (inferPluginOrigin !== 'web', i.e. isDraft 'fromPlugin')
 * reads as "plugin", everything else as "web". The two signals are intentionally
 * kept separate so the channel is never collapsed into the brand.
 *
 * @param {object} data - the url doc data
 * @param {object} [opts]
 * @param {string} [opts.signUpProduct] - owner's signUpProduct, forwarded to inferBrand
 * @returns {string}
 */
export function brandChannelLabel(data, opts = {}) {
    const brand = inferBrand(data, opts);
    const channel = inferPluginOrigin(data) === 'web' ? 'web' : 'plugin';
    return `${BRAND_DISPLAY[brand] || 'Figmafolio'} ${channel}`;
}
